<template>
  <div
    class="play"
    :class="{ 'show-top-bar': !isPremium, 'show-teaser': showTeaser }"
  >
    <loader v-if="loading"></loader>

    <div class="ui container" v-else-if="error">
      <div class="ui red message">{{ error }}</div>
      <a
        class="ui right labeled icon orange massive fluid button"
        :href="homePage"
      >
        <i class="home icon"></i> Keer Terug
      </a>
    </div>

    <div class="ui fluid container" v-else>
      <top-bar v-if="!isPremium" link="https://kwis.app/prijzen">
        Gratis basisversie!
        <a target="_blank" href="https://kwis.app/prijzen">Ontdek hier</a> wat
        je allemaal mist. 🤔😍
      </top-bar>
      <Teaser v-if="showTeaser" />
      <transition name="fade" mode="out-in" :appear="true">
        <!-- State Screens -->
        <host-or-player-picker v-if="!screenModeIsSet"></host-or-player-picker>
        <lobby v-else-if="showLobby"></lobby>
        <edit-scores v-else-if="showEditScores"></edit-scores>
        <round-intro v-else-if="showRoundIntro"></round-intro>
        <players-turn v-else-if="showPlayersTurn"></players-turn>
        <game-over v-else-if="showGameOver"></game-over>
        <!-- Rounds -->
        <drie-zes-negen v-else-if="game.currentRound == 0"></drie-zes-negen>
        <open-deur v-else-if="game.currentRound == 1"></open-deur>
        <puzzel v-else-if="game.currentRound == 2"></puzzel>
        <galerij v-else-if="game.currentRound == 3"></galerij>
        <collectief-geheugen
          v-else-if="game.currentRound == 4"
        ></collectief-geheugen>
        <finale v-else-if="game.currentRound == 5"></finale>
      </transition>
      <transition name="fade" mode="out-in" :appear="true">
        <div class="ui center aligned container" v-if="imgPreloader.loading">
          <div class="ui hidden divider"></div>
          <div class="ui active inline text huge inverted loader">
            {{ imgPreloader.loaded }}/{{ imgPreloader.total }} foto's
          </div>
        </div>
      </transition>
      <host-menu v-if="isHost && !gameEnded"></host-menu>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import HostMenu from './elements/hostMenu';
import Loader from './loader';
import Lobby from './lobby';
import EditScores from './editScores';
import GameOver from './gameOver';
import PlayersTurn from './playersTurn';
import RoundIntro from './roundIntro';
import HostOrPlayerPicker from './hostOrPlayerPicker';
import DrieZesNegen from './rounds/3-6-9/main';
import OpenDeur from './rounds/open-deur/main';
import Puzzel from './rounds/puzzel/main';
import Galerij from './rounds/galerij/main';
import TopBar from './elements/topBar';
import Teaser from './elements/teaser';
import CollectiefGeheugen from './rounds/collectief-geheugen/main';
import Finale from './rounds/finale/main';
import preloadImages from '../utils/imagePreloader';
// import alertify from 'alertify.js';
import { socketService } from '../api/socketService';
import Mousetrap from 'mousetrap';
import sounds from '@/sounds';
import { setHtmlClass } from '@/utils/bgColor';
import { identify } from '@/utils/heap';

export default {
  name: 'play',
  components: {
    HostMenu,
    Loader,
    Lobby,
    EditScores,
    GameOver,
    PlayersTurn,
    RoundIntro,
    HostOrPlayerPicker,
    DrieZesNegen,
    OpenDeur,
    Puzzel,
    Galerij,
    CollectiefGeheugen,
    Finale,
    TopBar,
    Teaser
  },
  props: ['gameId', 'pinCode'],
  data() {
    return {
      error: null,
      loading: false,
      imgPreloader: {
        loading: false,
        loaded: 0,
        total: 0
      }
    };
  },
  computed: {
    ...mapGetters([
      'screenModeIsSet',
      'isHost',
      'isPremium',
      'showLobby',
      'showRoundIntro',
      'showEditScores',
      'showPlayersTurn',
      'showGameOver',
      'gameEnded',
      'showTeaser',
      'allImgUrlsFromQuestions'
    ]),
    ...mapState(['game', 'hostId', 'quiz', 'name', 'homePage'])
  },
  created() {
    this.fetchData();
    this.addKeyboardShortcuts();
  },
  mounted() {
    // -> setup sound
    sounds.setup();
  },
  // [todo]
  // beforeDestroy() {
  //   // -> unsubscribe from realtime changes
  //   if (!this.isHost) {
  //     this.unwatchGame();
  //   }
  // },
  methods: {
    ...mapActions([
      'fetchQuiz',
      'watchGameStateChanges',
      'openHostScreen',
      'toggleFullscreen'
    ]),
    fetchData() {
      this.loading = true;
      // -> Get all quiz data from the api: questions, players' names,...
      this.fetchQuiz(this.gameId)
        // -> Watch game state changes
        .then(() => {
          socketService.init(this.gameId);
          this.watchGameStateChanges();
        })
        .then(() => {
          // -> Change the head of the HTML page
          document.title = this.name;
          // -> If there is a pinCode in the url then try to open the host screen
          if (this.pinCode) this.openHostScreen(this.pinCode);
          // -> Start preloading all images in the background
          this.preloadAllImages();
          // -> Set the correct BG Color and setup listener
          setHtmlClass(this.game.bgColor);
          this.listenToBgChanges();
          // -> Everything is loaded, hide the spinner
          this.loading = false;
          // -> send user identity to Heap Analytics
          identify(this.hostId);
        })
        .catch(error => {
          this.loading = false;
          // console.error(error);
          this.error =
            error.response && error.response.data && error.response.data.error
              ? error.response.data.error
              : error;
        });
    },
    preloadAllImages() {
      this.imgPreloader.loading = true;
      this.imgPreloader.total = preloadImages(
        this.allImgUrlsFromQuestions,
        currentImage => {
          this.imgPreloader.loaded = currentImage;
        },
        currentImage => {
          this.imgPreloader.loaded = currentImage;
        },
        () => {
          // Set a timeout to prevent a short distracting flicker
          setTimeout(() => {
            this.imgPreloader.loading = false;
          }, 1000);
        }
      );
    },
    addKeyboardShortcuts() {
      Mousetrap.bind('f', () => {
        this.toggleFullscreen();
        return false;
      });
    },
    listenToBgChanges() {
      this.$store.watch(
        state => state.game.bgColor,
        newColor => setHtmlClass(newColor)
      );
    }
  }
};
</script>
